import React, { useContext } from "react";
import { IMPORT_FILE_ACCEPT, formatDay } from "../api/shape.js";
import { PhoneShellContext, PhoneTopRow, PhoneFullScreen, Svg, ICON } from "./PhoneShell.jsx";
import PhoneUploadDaily from "./PhoneUploadDaily.jsx";
import AddInspection from "../screens/day/AddInspection.jsx";

function AssignActions({ line, lane, people, pending, onAssign }) {
  const { sm, assistants } = people;
  const toAssistant =
    assistants.length > 1 ? (
      <select className="ph-dy-select" aria-label="To assistant" disabled={pending} value="" onChange={(e) => e.target.value && onAssign(line, e.target.value)}>
        <option value="">To assistant…</option>
        {assistants.map((a) => <option key={a.upn} value={a.upn}>{a.name}</option>)}
      </select>
    ) : (
      <button type="button" className="ph-dy-act" disabled={pending || assistants.length === 0} onClick={() => onAssign(line, assistants[0].upn)}>
        {pending ? "…" : lane === "mine" ? "Move to assistant" : "To assistant"}
      </button>
    );
  const toMe = (
    <button type="button" className="ph-dy-act" disabled={pending || !sm} onClick={() => onAssign(line, sm.upn)}>
      {lane === "theirs" ? "Move to site manager" : "To me"}
    </button>
  );
  if (lane === "unassigned") return <div className="ph-dy-two">{toMe}{toAssistant}</div>;
  const toNobody = (
    <button type="button" className="ph-dy-act" disabled={pending} onClick={() => onAssign(line, "")}>
      {pending ? "…" : "Move to not assigned"}
    </button>
  );
  return <div className="ph-dy-two">{lane === "mine" ? toAssistant : toMe}{toNobody}</div>;
}

function SideRow({ line, side }) {
  const text = side.done
    ? side.result || "Recorded"
    : side.postponed
      ? "Postponed"
      : side.carried
        ? `Carried to ${side.carriedTo ? formatDay(`${side.carriedTo}T00:00:00Z`) : "the next working day"}`
        : side.notAttended
          ? `Not attended: ${side.notAttendedReason}`
          : side.defectText || "No earlier inspection";
  return (
    <div className="ph-dy-side">
      <span className="s">{side.label}</span>
      <span className="t">{text}</span>
      {side.done ? <span className="ph-dy-chip ok">DONE</span> : null}
    </div>
  );
}

function LineCard({ line, lane, people, pending, result, onAssign, showCode }) {
  return (
    <div className={`ph-dy-card${lane === "unassigned" ? " warn" : ""}`} data-testid="day-line" data-lane={lane}>
      <div className="ph-dy-top">
        <span className="ph-dy-time">{line.timeText}</span>
        <span className={`ph-dy-chip ${line.chip.tone === "mute" ? "" : line.chip.tone}`}>{line.chip.text}</span>
      </div>
      <span className="ph-dy-item">{line.item}</span>
      <span className="ph-dy-place">{showCode ? `${line.projectCode} · ${line.place}` : line.place}</span>
      {line.repair && line.defectsPhone ? <span className="ph-dy-defect">{line.defectsPhone}</span> : null}
      {line.noSide && !line.done && !line.sides[0].closed ? (
        <span className="ph-dy-muted">No side on the yard&apos;s list</span>
      ) : (
        line.sides.map((s) => <SideRow key={s.scheduleRef} line={line} side={s} />)
      )}
      {result ? <span role="alert" className="ph-dy-defect">{result.message}</span> : null}
      {line.open ? <AssignActions line={line} lane={lane} people={people(line.projectCode)} pending={pending} onAssign={onAssign} /> : null}
    </div>
  );
}

function Lane({ title, heading, lane, lines, ...rest }) {
  return (
    <section className="ph-section" style={{ gap: 10 }} data-testid={`day-lane-${lane}`}>
      <h2 className={`ph-dy-who${lane === "unassigned" ? " warn" : ""}`}>{heading}</h2>
      {lines.map((line) => (
        <LineCard key={line.key} line={line} lane={lane} pending={rest.pendingKey === line.key} result={rest.results[line.key]} people={rest.people} onAssign={rest.onAssign} showCode={rest.showCode} />
      ))}
    </section>
  );
}

function EmptyDay({ day, upload }) {
  return (
    <>
      <section className="ph-dy-empty" data-testid="day-empty">
        <h2>No yard list for today yet</h2>
        <p>When the yard&apos;s QC sends today&apos;s inspection list on WeChat, save the file to your phone and upload it here.</p>
        <button type="button" className="ph-dy-upload" data-testid="today-upload-button" onClick={upload.open}>
          <Svg size={18} stroke={2.2}>{ICON.download}</Svg>Upload today&apos;s list
        </button>
      </section>
      {day.expectedCount ? (
        <section className="ph-section">
          <h2 className="ph-dy-who">Expected today</h2>
          <div className="ph-dy-dashed">
            <span className="a">About {day.expectedCount} inspections</span>
            <span className="ph-dy-muted">From the yard&apos;s block schedule. These are not requested until they appear on the daily list.</span>
          </div>
        </section>
      ) : null}
      {day.carried.length ? (
        <section className="ph-section" data-testid="day-carried">
          <h2 className="ph-dy-who">Still open from yesterday</h2>
          <div className="ph-dy-link">
            <span className="ph-stack grow">
              <span className="ph-dy-item">{day.carried.length} {day.carried.length === 1 ? "inspection" : "inspections"} not recorded</span>
              <span className="ph-dy-muted">{day.carried.map((c) => c.text).join("; ")}. Carried to today; it merges with the same line when today&apos;s list arrives.</span>
            </span>
          </div>
        </section>
      ) : null}
    </>
  );
}

/**
 * Today on a phone (TodayDay.dc.html, TodayDayEmpty.dc.html, UploadDaily.dc.html).
 * `day` is shape.js#shapeDayScreen's output: the same model the desktop renders.
 */
export default function PhoneToday({
  day, errors = [], notice, people, pendingKey, results, onAssign, onAdd, projectCodes, upload,
  importPanel, onImportCommit, onImportDismiss, addOpen, setAddOpen, onNavigate,
}) {
  const shell = useContext(PhoneShellContext);
  const addCodes = day.projectCodes.length ? day.projectCodes : projectCodes;
  const laneProps = { people, pendingKey, results, onAssign, showCode: day.multiProject };

  if (importPanel) {
    return (
      <>
        <input ref={upload.ref} type="file" accept={IMPORT_FILE_ACCEPT} data-testid="today-import-file-input" style={{ display: "none" }} onChange={upload.onChange} />
        <PhoneUploadDaily panel={importPanel} onChange={upload.open} onCommit={onImportCommit} onDismiss={onImportDismiss} />
      </>
    );
  }
  if (addOpen) {
    return (
      <PhoneFullScreen title="Add one inspection" titleId="ph-dy-add-title" cancelStyle backLabel="Cancel" onBack={() => setAddOpen(false)} testId="ph-today-add">
        <AddInspection phone projects={addCodes} date={day.date} onSave={onAdd} onClose={() => setAddOpen(false)} />
      </PhoneFullScreen>
    );
  }

  return (
    <div className="ph-screen" data-testid="phone-today">
      <header className="ph-head">
        <PhoneTopRow />
        <div className="ph-titlerow base">
          <h1 className="ph-h1">Today</h1>
          <span className="ph-meta">{day.dateLabel || (shell && shell.weekLabel) || ""}</span>
        </div>
      </header>
      <main className="ph-dy-main">
        <input ref={upload.ref} type="file" accept={IMPORT_FILE_ACCEPT} data-testid="today-import-file-input" style={{ display: "none" }} onChange={upload.onChange} />
        {errors.length > 0 ? (
          <section className="ph-section" data-testid="today-errors">
            {errors.map((e) => (
              <div className="ph-card" key={e.code}><span className="ph-cardsub" style={{ color: "var(--ph-bad)" }}>{e.code}: unavailable &mdash; {e.error}</span></div>
            ))}
          </section>
        ) : null}
        {notice ? <div role="alert" className="ph-card warnbox" data-testid="today-notice">{notice}</div> : null}

        {!day.hasList ? (
          <EmptyDay day={day} upload={upload} />
        ) : (
          <>
            <section className="ph-dy-summary" aria-label="Progress today">
              <div className="ph-dy-figures">
                <span className="req">{day.requestedText}</span>
                <span className="rec">{day.recordedText}</span>
                <span className="togo">{day.toGoText}</span>
                {day.postponedText ? <span className="ph-dy-muted">{day.postponedText}</span> : null}
                {day.closedText ? <span className="ph-dy-muted" data-testid="day-closed-figures">{day.closedText}</span> : null}
              </div>
              <div className="ph-dy-bar" aria-hidden="true"><div style={{ width: day.pct }} /></div>
              <span className="ph-dy-muted">Counted per side.{day.importedAt ? ` ${day.importText}.` : ""}</span>
              <span className="ph-dy-muted">{day.unscheduledText}</span>
            </section>

            {day.unassigned.lines.length > 0 ? (
              <Lane lane="unassigned" heading={`Not assigned yet · ${day.unassigned.lines.length}`} lines={day.unassigned.lines} {...laneProps} />
            ) : null}
            {day.mine.lines.length > 0 ? (
              <Lane lane="mine" heading={`Site manager · ${day.mine.recorded} of ${day.mine.sides} recorded`} lines={day.mine.lines} {...laneProps} />
            ) : null}
            {day.theirs.lines.length > 0 ? (
              <Lane lane="theirs" heading={`Assistant inspector · ${day.theirs.recorded} of ${day.theirs.sides} recorded`} lines={day.theirs.lines} {...laneProps} />
            ) : null}
          </>
        )}

        {day.hasList && day.carried.length ? (
          <section className="ph-section" data-testid="day-carried">
            <h2 className="ph-dy-who">Not recorded yesterday · {day.carried.length}</h2>
            <div className="ph-dy-link"><span className="ph-dy-muted">{day.carried.map((c) => (day.multiProject ? `${c.projectCode} · ${c.text}` : c.text)).join("; ")}</span></div>
          </section>
        ) : null}
        {day.hasList ? (
          <>
            <div className="ph-dy-two">
              <button type="button" className="ph-dy-act" data-testid="today-add-button" onClick={() => setAddOpen(true)}>Add one inspection</button>
              <button type="button" className="ph-dy-act" data-testid="today-upload-button" onClick={upload.open}>Upload a new list</button>
            </div>
            {day.canEndDay ? <button type="button" className="ph-dy-act ph-dy-eod" data-testid="today-eod-button" onClick={() => onNavigate && onNavigate("endofday")}>End of day</button> : null}
          </>
        ) : (
          <>
            <button type="button" className="ph-dy-act" data-testid="today-add-button" onClick={() => setAddOpen(true)}>Add one inspection</button>
            {day.canEndDay ? <button type="button" className="ph-dy-act ph-dy-eod" data-testid="today-eod-button" onClick={() => onNavigate && onNavigate("endofday")}>End of day</button> : null}
          </>
        )}
      </main>
    </div>
  );
}
